import type { ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { useLiveSync } from "../../store/hooks.js";
import { RouteErrorBoundary } from "../../components/ErrorBoundary.js";
import { useAuth, useLogout } from "../auth/useAuth.js";

export function AppShell({ children }: { children: ReactNode }) {
  const { user, authEnabled, canView: isAuthed } = useAuth();
  const wsState = useLiveSync(isAuthed);
  const logout = useLogout();

  const wsCls =
    wsState === "open"
      ? "app-status live"
      : wsState === "closed"
        ? "app-status disconnected"
        : "app-status";
  const wsLabel =
    wsState === "open"
      ? "● live"
      : wsState === "closed"
        ? "● disconnected"
        : "● connecting…";

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header-left">
          <Link to="/" className="app-title">Agent Dashboard</Link>
          {isAuthed && (
            <nav className="app-nav">
              {/* NavLink adds the `active` class + aria-current="page" on match.
                  `end` keeps Live active only on exactly "/", not every route. */}
              <NavLink to="/" end>
                Live
              </NavLink>
              <NavLink to="/repos">Repos</NavLink>
              <NavLink to="/runs">Runs</NavLink>
              <NavLink to="/stats">Stats</NavLink>
              <NavLink to="/settings">Settings</NavLink>
            </nav>
          )}
        </div>
        {isAuthed && (
          <div className="app-header-right">
            <div className={wsCls}>{wsLabel}</div>
            {/* The API is open to anyone who can reach it; say so where the
                email/logout would otherwise sit. */}
            {!authEnabled && (
              <div
                className="app-user-email"
                title="AUTH_EMAIL / AUTH_PASSWORD_HASH / SESSION_SECRET are not set"
              >
                auth off
              </div>
            )}
            {user && (
              <>
                <div className="app-user-email">{user.email}</div>
                <button
                  type="button"
                  onClick={() => logout.mutate()}
                  disabled={logout.isPending}
                  style={{
                    background: "transparent",
                    color: "var(--muted)",
                    border: "1px solid var(--border)",
                    borderRadius: 4,
                    padding: "6px 12px",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                  title="Sign out"
                >
                  Sign out
                </button>
              </>
            )}
          </div>
        )}
      </header>
      <main>
        <RouteErrorBoundary>{children}</RouteErrorBoundary>
      </main>
    </div>
  );
}
